import {
    Component,
    OnInit,
    OnDestroy,
    ViewChild,
    TemplateRef,
    NO_ERRORS_SCHEMA,
    inject,
    Input,
    EventEmitter,
    Output,
    ElementRef,
    signal,
    Inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { UntypedFormArray, ReactiveFormsModule } from '@angular/forms';
import { PillsComponent } from '../shared/pills/pills.component';
import { MatIconModule } from '@angular/material/icon';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { TableCardLayoutComponent } from '../shared/table-card-layout/table-card-layout.component';
import { firstValueFrom, Subscription, combineLatest } from 'rxjs';
import { DynamicFormComponent } from '../shared/dynamic-form/dynamic-form.component';
import { SidePanelService } from '../shared/side-panel/side-panel.service';
import { ActivatedRoute } from '@angular/router';
import { LoadingService } from '../shared/loading.service';
import { Store } from '@ngrx/store';
import { PolicyState } from '../store/policy/policy.reducer';
import { filter } from 'rxjs/operators';
import { PolicyDetailsService } from '../services/policy-details.service';
import { NotesService } from '../services/notes.service';
import { refreshDCPolicy } from '../store/policy/policy.actions';
import { ToastService } from '../shared/toast/toast.service';
import { globalMessages } from '../shared/global-messages';
import { APP_CONFIG, AppConfig } from '../../app.config';
import { getAuthToken } from '../auth.interceptor';

@Component({
    selector: 'app-notes',
    imports: [
        CommonModule,
        PillsComponent,
        MatIconModule,
        ...PSIC_COMPONENTS,
        ReactiveFormsModule,
        DynamicFormComponent,
        TableCardLayoutComponent,
    ],
    templateUrl: './notes.component.html',
    styleUrl: './notes.component.scss',
    schemas: [NO_ERRORS_SCHEMA],
    providers: [SidePanelService],
})
export class NotesComponent implements OnInit, OnDestroy {
    @Input() isDCPolicyLoaded = false;
    @Input() dcPolicyFailed = false;
    @Output() retry = new EventEmitter<void>();
    globalMessages = globalMessages;
    notes: any[] = [];
    public policyNumber: string = '';
    private selectionSubscription?: Subscription;
    pills: any[] = [];
    selectable = false;
    filteredDataFilter: any[] = [];
    filteredNotes: any[] = [];
    totalRecords = 0;
    showAll = signal(false);
    cols: any[] = [];
    sortField = '';
    sortOrder = 1;
    sidePanelForm = new UntypedFormArray([]);
    sidePanelField: any = {};
    policyId: string = '';
    effectiveDate: string = '';
    expirationDate: string = '';
    quoteId: string = '';
    isViewMode = false;
    private combinedSubscription: Subscription;
    @ViewChild('sidePanelContent') sidePanelContent!: TemplateRef<any>;
    @ViewChild('sidePanelFooter') sidePanelFooter!: TemplateRef<any>;
    private policyDetails: any;
    private el = inject(ElementRef);
    private loadingService = inject(LoadingService);
    private policyService = inject(PolicyDetailsService);
    private notesService = inject(NotesService);

    constructor(
        private http: HttpClient,
        private sidePanelService: SidePanelService,
        private route: ActivatedRoute,
        private toastService: ToastService,
        private store: Store<{ policy: PolicyState }>,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {}

    retryDCPolicy() {
        this.retry.emit();
    }

    ngOnInit() {
        this.route.queryParams.subscribe((params) => {
            this.policyId = params?.policyNumber;
        });

        this.cols = [
            { field: 'callNumber', header: 'Call number', width: '150px' },
            { field: 'createdBy', header: 'Created by', width: '150px' },
            { field: 'date', header: 'Date', width: '200px' },
            { field: 'category', header: 'Category', width: '200px' },
            { field: 'note', header: 'Note', width: '300px' },
        ];

        this.sidePanelService.saveClicked.subscribe(() => this.onSaveNotes());
        this.sidePanelService.closeClicked.subscribe(() => this.cancelNotes());
        this.sidePanelService.cancelClicked.subscribe(() => this.cancelNotes());

        this.getNotesFields().then(() => {
            this.combinedSubscription = combineLatest([
                this.store.select((state) => state.policy.data).pipe(filter((data) => !!data)),
                this.store.select((state) => state.policy.policyNumber).pipe(filter((policyNumber) => !!policyNumber)),
            ]).subscribe(([policyData, policyNumber]) => {
                this.policyDetails = policyData;
                this.policyNumber = policyNumber!;
                this.effectiveDate =
                    policyData?.[policyNumber!]?.policySnapshot?.policyGeneralInformation?.effectiveDate?.value ?? '';
                this.expirationDate =
                    policyData?.[policyNumber!]?.policySnapshot?.policyGeneralInformation?.expirationDate?.value ?? '';
                this.quoteId =
                    policyData?.[policyNumber!]?.policySnapshot?.policyGeneralInformation?.quoteId?.value ?? '';
                this.loadNotes(this.policyNumber);
            });
        });
    }

    private getCategoryCaption(value: string): string {
        return (
            this.sidePanelField?.options?.optionsList?.[0]?.options?.optionsList?.find(
                (opt: any) => opt.value === value
            )?.caption || value
        );
    }

    private getSubCategoryCaption(value: string): string {
        const categories = this.sidePanelField?.options?.optionsList?.[0]?.options?.optionsList || [];
        for (const cat of categories) {
            const subCat = cat.actions?.[0]?.params?.optionsList?.find((opt: any) => opt.value === value);
            if (subCat) return subCat.caption;
        }
        return value;
    }

    private extractDateRanges(filters: any[]): { from: string; to: string }[] {
        const dateRanges: { from: string; to: string }[] = [];
        filters.forEach((filter: any) => {
            if (filter.items && Array.isArray(filter.items)) {
                filter.items.forEach((item: any) => {
                    if (item.selected && item.type === 'Date') {
                        dateRanges.push({ from: item.from, to: item.to });
                    }
                });
            }
        });
        return dateRanges;
    }

    private isDateInRanges(dateStr: string, ranges: { from: string; to: string }[]): boolean {
        const noteDate = dateStr.split('T')[0]; // extract "2026-03-24" only
        return ranges.some((range) => {
            const from = range.from.split('T')[0];
            const to = range.to.split('T')[0];
            return noteDate >= from && noteDate <= to;
        });
    }

    private loadNotes(policyKey: string) {
        const notesData = this.policyDetails[policyKey]?.policySnapshot?.notes || [];
        this.notes = notesData.map((n: any) => {
            const subjectValue = n.commentSubject?.value ?? n.callNumber?.value ?? '';
            const raw = subjectValue.match(/\+?([\d]+)$/)?.[1] ?? '';
            const callNumber = raw ? raw.replace(/^1(\d{10})$/, '$1') : 'N/A';
            return {
                callNumber,
                createdBy: n.createdBy.value,
                date: n.date.value?.replace(' ', 'T'),
                category: this.getCategoryCaption(n.category.value),
                categoryValue: n.category.value,
                subCategory: this.getSubCategoryCaption(n.subCategory?.value),
                subCategoryValue: n.subCategory?.value || '',
                note: n.note.value || '',
            };
        });
        this.totalRecords = this.notes.length;
        this.filteredNotes = this.notes;
        this.showAll.set(false);
        this.generateAggregates();
    }

    loadMoreNotes() {
        this.showAll.set(true);
    }

    showLessNotes() {
        this.showAll.set(false);
        this.el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    ngOnDestroy() {
        this.selectionSubscription?.unsubscribe();
        this.combinedSubscription?.unsubscribe();
    }

    refreshPage() {
        this.sortField = '';
        this.sortOrder = 1;
        this.filteredDataFilter = [];
        this.filteredNotes = this.notes;
        this.totalRecords = this.notes.length;
        this.showAll.set(false);
        this.generateAggregates();
    }

    filteredData(event: any) {
        this.filteredDataFilter = event;

        if (!event || event.length === 0) {
            this.filteredNotes = this.notes;
            this.totalRecords = this.notes.length;
            this.showAll.set(false);
            return;
        }

        event.forEach((filter: any) => {
            filter.items?.forEach((item: any) => {
                if (item.selected && item.type === 'Date' && item.to === '') {
                    item.to = item.from;
                }
            });
        });

        const dateRanges = this.extractDateRanges(event);
        this.filteredNotes =
            dateRanges.length > 0
                ? this.notes.filter((note) => this.isDateInRanges(note.date, dateRanges))
                : this.notes;
        this.totalRecords = this.filteredNotes.length;
        this.showAll.set(false);
    }

    generateAggregates() {
        const uniqueDates = [...new Set(this.notes.map((n) => n.date?.split('T')[0]))].filter((d) => d);
        this.pills = [
            {
                data: {
                    name: 'Date',
                    selectedItems: 0,
                    isOpened: false,
                    selectAll: false,
                    isFiltered: false,
                    items: uniqueDates.map((date) => ({
                        name: date,
                        value: date,
                        type: 'Date',
                        from: `${date}T00:00:00`,
                        to: `${date}T23:59:59`,
                        selected: false,
                    })),
                },
            },
        ];
    }

    sortColumn(event: any) {
        this.sortField = event.field;
        this.sortOrder = event.order;
    }

    addNewNotes() {
        this.isViewMode = false;
        this.sidePanelField.caption = 'Add Note';
        this.sidePanelField.options.optionsList.forEach((field: any) => {
            field.value = '';
            field.disabled = false;
            if (field.name === 'subCategory') {
                field.options = { optionsList: [] };
            }
        });
        this.sidePanelService.open({
            form: this.sidePanelForm,
            contentTemplate: this.sidePanelContent,
            footerTemplate: this.sidePanelFooter,
            data: {},
        });
    }

    onSaveNotes() {
        this.loadingService.showLoading();

        const noteValue = this.sidePanelForm.controls[0].value.note;
        const categoryValue = this.sidePanelForm.controls[0].value.category;
        const subCategoryValue = this.sidePanelForm.controls[0].value.subCategory;

        // close panel immediately — values already captured above
        this.sidePanelService.closeAll();
        this.sidePanelForm.clear();

        const saveNote = (quoteId: string, effectiveDate: string) => {
            const body = {
                policyId: quoteId,
                policyEffectiveDate: effectiveDate,
                commentTypeCode: 'CI',
                commentText: noteValue,
                commentSubject: 'New Notes',
                commentCategory: categoryValue,
                commentSubCategory: subCategoryValue,
                action: 'ADD_NOTES',
            };
            this.notesService.addNotes(quoteId, body).subscribe({
                next: () => {
                    this.loadingService.hideLoading();
                    this.store.dispatch(
                        refreshDCPolicy({
                            policyNumber: this.policyNumber,
                            effectiveDate: this.effectiveDate || undefined,
                            expireDate: this.expirationDate || undefined,
                        })
                    );
                    this.toastService.add({
                        type: 'success',
                        title: '',
                        message: 'The note was successfully generated.',
                    });
                },
                error: () => {
                    this.loadingService.hideLoading();
                    this.toastService.add({ type: 'error', title: '', message: 'Failed to add note.' });
                },
            });
        };

        if (this.quoteId) {
            saveNote(this.quoteId, this.effectiveDate);
        } else {
            this.http
                .post<any>(this.config.quoteSearchApi, {
                    InputType: 'Policy',
                    search: this.policyNumber,
                    token: getAuthToken(),
                })
                .subscribe({
                    next: (res) => {
                        const hits: any[] = res?.hits ?? [];
                        const latest = hits.sort((a, b) => Number(b.Term) - Number(a.Term))[0];
                        const quoteId = latest?.QuoteID ?? '';
                        const effectiveDate = latest?.EffectiveDate ?? this.effectiveDate;
                        saveNote(quoteId, effectiveDate);
                    },
                    error: () => {
                        this.loadingService.hideLoading();
                        this.toastService.add({ type: 'error', title: '', message: 'Failed to add note.' });
                    },
                });
        }
    }

    cancelNotes() {
        this.sidePanelService.closeAll();
        this.sidePanelForm.clear();
    }

    async getNotesFields(): Promise<void> {
        const res: any = await firstValueFrom(this.http.get('assets/policy_edit/AddNotes.json'));
        this.sidePanelField = res.data.fields[0];
        this.sidePanelField.options.optionsList[1].options = { optionsList: [] };
    }
}
